import withStyles from '@mui/styles/withStyles';
import _ from 'lodash';
import classNames from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';
import SearchStore from '../../../stores/SearchStore';
import SearchDataWindow from './SearchDataWindow.react';
import SEARCH_CONSTANTS from 'arbitrip-common/general/constants/search-constants';
import { withTranslation } from 'react-i18next';

const styles = (theme) => ({
	root: {
		alignSelf: 'center',
		textAlign: 'start',
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
	},
	label: {
		fontSize: 15,
		color: theme.palette.gray.gray2,
		margin: theme.direction === 'rtl' ? '0 0 0 2px' : '0 2px 0 0',
	},
	contentWrap: {
		display: 'flex',
		alignItems: 'center',
		whiteSpace: 'nowrap',
		fontSize: 15,
		fontWeight: 600,
		color: theme.palette.black.main,
		width: '100%',
		justifyContent: theme.direction === 'rtl' ? 'flex-end' : 'flex-start',
		flexDirection: theme.direction === 'rtl' ? 'row-reverse' : 'row',
	},
	content: {
		margin: '0 2px',
	},
});

function getState() {
	return {
		private_travel: SearchStore.getPrivateTravel(),
	};
}

class SearchDataComponent extends React.Component {
	constructor(props) {
		super(props);

		this.toggleWindow = this.toggleWindow.bind(this);
		this.closeWindow = this.closeWindow.bind(this);
		this.setNewCount = this.setNewCount.bind(this);
		this.updateChildrenAges = this.updateChildrenAges.bind(this);
		this.updateSingleChildrenAge = this.updateSingleChildrenAge.bind(this);
		this._onChange = this._onChange.bind(this);

		this.state = {
			showWindow: false,
			adults: props.adults || SEARCH_CONSTANTS.MIN.ADULTS,
			rooms: props.rooms || SEARCH_CONSTANTS.MIN.ROOMS,
			children: props.childrenAges ? props.childrenAges.length : SEARCH_CONSTANTS.MIN.CHILDREN,
			childrenAges: props.childrenAges,
			...getState(),
		};
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevProps.rooms !== this.props.rooms) {
			this.setState({ rooms: this.props.rooms });
		}

		if (prevProps.adults !== this.props.adults) {
			this.setState({ adults: this.props.adults });
		}

		if (this.state.adults !== prevState.adults) {
			if (_.isFunction(this.props.onGuestsChange)) {
				this.props.onGuestsChange(this.state.adults);
			}
		}

		if (!_.isEqual(prevProps.childrenAges, this.props.childrenAges)) {
			this.setState({ children: this.props.childrenAges.length, childrenAges: this.props.childrenAges });
		}

		if (this.state.children !== prevState.children) {
			this.updateChildrenAges();
		}

		if (this.state.rooms !== prevState.rooms) {
			if (_.isFunction(this.props.onRoomsChange)) {
				this.props.onRoomsChange(this.state.rooms);
			}
		}
	}

	updateChildrenAges() {
		let { children, childrenAges } = this.state;
		let { onChildrenChange } = this.props;
		if (!childrenAges) {
			childrenAges = [];
		}

		if (children > 0 && !childrenAges[children - 1]) {
			childrenAges = this.updateSingleChildrenAge(children - 1, SEARCH_CONSTANTS.DEFAULT_CHILD_AGE);
		}

		childrenAges.forEach((age, index) => {
			if (index + 1 > children) {
				childrenAges.splice(index, 1);
			}
		});

		if (_.isFunction(onChildrenChange)) {
			onChildrenChange(childrenAges);
		}
	}

	updateSingleChildrenAge(childNumber, age) {
		let { onChildrenChange } = this.props;
		let { childrenAges } = this.state;
		if (!childrenAges) {
			childrenAges = [];
		}

		childrenAges[childNumber] = age;

		if (_.isFunction(onChildrenChange)) {
			onChildrenChange(childrenAges);
		}

		return childrenAges;
	}

	toggleWindow(forceOpenOrClose = null) {
		let { showWindow } = this.state;

		if (forceOpenOrClose !== null) {
			showWindow = forceOpenOrClose;
		} else {
			showWindow = !showWindow;
		}

		this.setState({ showWindow }, () => {
			this.props.onDataWindowToggle(showWindow);
		});
	}

	closeWindow() {
		this.setState({ showWindow: false });
	}

	setNewCount(name, count) {
		this.setState({ [name.toLowerCase()]: count });
	}

	render() {
		let { classes, showChildrenAgesError, t } = this.props;
		let { showWindow, adults, children, childrenAges, rooms } = this.state;

		const adultsCount = adults * (rooms || 1);
		const childrenCount = children * (rooms || 1);

		return (
			<div className={classes.root} id="search-data-root" data-testid="guests-summary">
				<span
					className={classNames(classes.label, {
						[classes.label]: !this.state.showWindow,
					})}
				>
					{t('Search.Guests')}
				</span>
				<div className={classNames(classes.contentWrap, 'search-data-content-wrap')}>
					<div
						id="adults"
						className={classNames(
							classes.content,
							SEARCH_CONSTANTS.SHARED_ATTRIBUTES_NAMES.SEARCH_DATA_CONTENT,
						)}
					>
						{adultsCount} {adultsCount > 1 ? t('Search.Adults') : t('Search.Adult')}
					</div>
					<div>&nbsp;·&nbsp;</div>
					<div
						id="rooms"
						className={classNames(
							classes.content,
							SEARCH_CONSTANTS.SHARED_ATTRIBUTES_NAMES.SEARCH_DATA_CONTENT,
						)}
					>
						{rooms} {rooms > 1 ? t('Search.Rooms') : t('Search.Room')}
					</div>
					{childrenCount > 0 && (
						<React.Fragment>
							<div>&nbsp;·&nbsp;</div>
							<div id="children" className={classes.content}>
								{childrenCount} {childrenCount > 1 ? t('Search.Children') : t('Search.Child')}
							</div>
						</React.Fragment>
					)}
				</div>
				{showWindow && (
					<SearchDataWindow
						adults={adults}
						childrenAges={childrenAges}
						rooms={rooms}
						setNewCount={this.setNewCount}
						closeWindow={this.toggleWindow}
						updateSingleChildrenAge={this.updateSingleChildrenAge}
						showChildrenAgesError={showChildrenAgesError}
						isAgent={this.props.isAgent}
					/>
				)}
			</div>
		);
	}

	_onChange() {
		this.setState(getState());
	}

	componentDidMount() {
		if (this.props.forwardedRef) {
			const boundToggleWindow = this.toggleWindow.bind(this);
			const boundCloseWindow = this.closeWindow.bind(this);

			this.props.forwardedRef.current = {
				toggleWindow: boundToggleWindow,
				closeWindow: boundCloseWindow,
			};
		}
		SearchStore.addChangeListener(this._onChange);
	}

	componentWillUnmount() {
		SearchStore.removeChangeListener(this._onChange);
	}
}

SearchDataComponent.propTypes = {
	classes: PropTypes.object.isRequired,
	adults: PropTypes.number,
	rooms: PropTypes.number,
	childrenAges: PropTypes.array,
	onGuestsChange: PropTypes.func.isRequired,
	onRoomsChange: PropTypes.func.isRequired,
	onChildrenChange: PropTypes.func.isRequired,
	onDataWindowToggle: PropTypes.func.isRequired,
	showChildrenAgesError: PropTypes.bool.isRequired,
	isAgent: PropTypes.bool,
	forwardedRef: PropTypes.object,
};

const StyledSearchData = withStyles(styles)(SearchDataComponent);

const TranslatedSearchData = withTranslation()(StyledSearchData);

const SearchData = React.forwardRef((props, ref) => <TranslatedSearchData {...props} forwardedRef={ref} />);

export default SearchData;
